import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Sparkles, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { GEN, REFS } from "../lib/api";

const slides = [REFS.ref4, REFS.ref1, REFS.ref5, GEN.actionLeap, REFS.ref2];

export const Hero = ({ onJoin, onSubscribe, onEarlyAccess }) => {
  const [index, setIndex] = useState(0);
  const ref = useRef(null);

  // subtle scroll parallax on the ambient background
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  // subtle mouse parallax on the image card
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 15 });

  const next = () => setIndex((i) => (i + 1) % slides.length);
  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);

  useEffect(() => {
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section
      id="universe"
      ref={ref}
      className="relative min-h-[100svh] overflow-hidden bg-[#050806] pt-[68px]"
      data-testid="hero-section"
    >
      {/* Ambient blurred backdrop (never behind the text block, heavily darkened) */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <AnimatePresence mode="popLayout">
          <motion.img
            key={index}
            src={slides[index]}
            alt=""
            aria-hidden="true"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 0.22, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover blur-2xl"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#050806] via-[#050806]/85 to-[#050806]/40" />
      <div className="absolute inset-0 halftone opacity-20" />
      <div className="absolute inset-0 vignette" />

      {/* Content */}
      <div className="relative z-10 max-w-[1500px] mx-auto px-5 sm:px-8 min-h-[calc(100svh-68px)] grid lg:grid-cols-2 gap-8 lg:gap-12 items-center py-10">
        {/* Left: text */}
        <div className="order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 2.7, duration: 0.6 }}
            className="font-accent text-lg sm:text-xl tracking-[0.3em] text-emerald-400 mb-3"
          >
            NIGERIA MADE · 100% NAIJA PRIDE
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.85, duration: 0.7 }}
            className="comic-title text-5xl sm:text-6xl lg:text-7xl xl:text-8xl"
            data-testid="hero-headline"
          >
            Hero of the Streets,<br />
            <span className="text-amber-500">Pride of the Nation</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.0, duration: 0.6 }}
            className="text-gray-300 text-base sm:text-lg max-w-xl mt-5"
          >
            When the lights went out over Lagos, a surge of green power woke a legend. Enter the world of NAIJAMAN — Nigeria&rsquo;s unstoppable defender.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.15, duration: 0.6 }}
            className="flex flex-wrap gap-4 mt-8"
          >
            <button onClick={onJoin} className="btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-lg sm:text-xl tracking-wider px-6 sm:px-7 py-3 glow-green transition-all duration-200 hover:scale-105" data-testid="hero-join-btn">
              <span className="flex items-center gap-2"><Sparkles size={18} /> Join the Universe</span>
            </button>
            <button onClick={onSubscribe} className="btn-skew bg-amber-500 hover:bg-amber-400 text-black font-accent text-lg sm:text-xl tracking-wider px-6 sm:px-7 py-3 glow-gold transition-all duration-200 hover:scale-105" data-testid="hero-subscribe-btn">
              <span className="flex items-center gap-2"><BookOpen size={18} /> Subscribe to Read Comics</span>
            </button>
            <button onClick={onEarlyAccess} className="btn-skew bg-white/5 hover:bg-white/15 border border-white/25 text-white font-accent text-lg sm:text-xl tracking-wider px-6 sm:px-7 py-3 transition-all duration-200 hover:scale-105" data-testid="hero-early-access-btn">
              <span className="flex items-center gap-2">Get Early Access <ArrowRight size={18} /></span>
            </button>
          </motion.div>

          <div className="flex gap-2 mt-9" data-testid="hero-carousel-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-10 bg-emerald-400" : "w-4 bg-white/30 hover:bg-white/50"}`}
                aria-label={`slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Right: image carousel card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.7, duration: 0.7 }}
          className="order-1 lg:order-2 flex justify-center lg:justify-end"
          style={{ perspective: 1200 }}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
        >
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative w-full max-w-[300px] sm:max-w-[380px] lg:max-w-[440px] aspect-[3/4] rounded-2xl overflow-hidden border-2 border-emerald-500/30 glow-green"
            data-testid="hero-carousel"
          >
            <AnimatePresence mode="popLayout">
              <motion.img
                key={index}
                src={slides[index]}
                alt={`NAIJAMAN — key art ${index + 1}`}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </AnimatePresence>
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl pointer-events-none" />

            {/* Arrows */}
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-black/50 hover:bg-emerald-500 hover:text-emerald-950 text-white border border-white/20 backdrop-blur transition-colors" aria-label="previous" data-testid="hero-prev">
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-black/50 hover:bg-emerald-500 hover:text-emerald-950 text-white border border-white/20 backdrop-blur transition-colors" aria-label="next" data-testid="hero-next">
              <ChevronRight size={20} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
